import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { useTerm } from "@/lib/terminology.jsx";
import DealMultiPicker from "@/components/DealMultiPicker";

/**
 * v5.13-r2 TaskModal — used for New Task and Follow-up creation.
 *
 * Props:
 *  - onClose(): void
 *  - onCreated(newTaskId): void
 *  - prefill: {task_name, description, assigned_to_id, priority, reviewer_enabled,
 *              reviewer_id, deal_ids, duplicated_from_task_id}
 *  - defaultDealId: string  // pre-added to Related Deals
 *  - defaultPipelineId: string  // seeds the picker
 *  - title, submitLabel: overrides
 */
export default function TaskModal({
  onClose,
  onCreated,
  prefill = null,
  defaultDealId = null,
  defaultPipelineId = null,
  title = null,
  submitLabel = null,
}) {
  const { user } = useAuth();
  const term = useTerm();
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    task_name: prefill?.task_name || "",
    description: prefill?.description || "",
    assigned_to_id: prefill?.assigned_to_id || user?.user_id || "",
    due_date: "",
    priority: prefill?.priority || "medium",
    reminder_enabled: false,
    remind_on: "",
    reviewer_enabled: !!prefill?.reviewer_enabled,
    reviewer_id: prefill?.reviewer_id || "",
  });

  useEffect(() => {
    api.get("/team-members").then(({ data }) => setTeamMembers(data));
  }, []);

  // Seed initial deals from prefill + defaultDealId
  useEffect(() => {
    const seed = async () => {
      const ids = [];
      if (Array.isArray(prefill?.deal_ids)) ids.push(...prefill.deal_ids);
      if (defaultDealId && !ids.includes(defaultDealId)) ids.push(defaultDealId);
      if (!ids.length) return;
      const results = [];
      for (const id of ids) {
        try {
          const { data } = await api.get(`/deals/${id}`);
          const d = data?.deal;
          if (d) {
            results.push({
              deal_id: d.deal_id,
              company_name: d.company_name,
              pipeline_id: d.pipeline_id,
            });
          }
        } catch {
          /* ignore missing */
        }
      }
      setSelectedDeals(results);
    };
    seed();
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.task_name.trim()) {
      toast.error(`${term("Task")} name required`);
      return;
    }
    if (!form.assigned_to_id) {
      toast.error("Please choose an assignee");
      return;
    }
    if (!form.due_date) {
      toast.error("Please choose a due date");
      return;
    }
    if (form.reviewer_enabled && !form.reviewer_id) {
      toast.error("Please choose a reviewer");
      return;
    }
    if (form.reminder_enabled && !form.remind_on) {
      toast.error("Please choose a reminder date");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        task_name: form.task_name.trim(),
        description: form.description.trim() || null,
        assigned_to_id: form.assigned_to_id,
        due_date: form.due_date,
        priority: form.priority,
        reminder_enabled: !!form.reminder_enabled,
        remind_on: form.reminder_enabled ? form.remind_on : null,
        reviewer_enabled: !!form.reviewer_enabled,
        reviewer_id: form.reviewer_enabled ? form.reviewer_id : null,
        deal_ids: selectedDeals.map((d) => d.deal_id),
        duplicated_from_task_id: prefill?.duplicated_from_task_id || null,
      };
      const { data } = await api.post("/v513/tasks", payload);
      toast.success(`${term("Task")} created`);
      onCreated?.(data.task_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-full h-9 px-3 rounded-md border text-sm mt-1";
  const border = { borderColor: "var(--cw-border)" };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="task-modal"
    >
      <form
        onSubmit={submit}
        className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] flex flex-col"
      >
        <div className="px-6 py-4 border-b flex justify-between items-center" style={border}>
          <h2 className="cw-h2">{title || `New ${term("Task")}`}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--cw-muted)] text-lg"
            data-testid="task-modal-close"
          >
            ×
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-3">
          <F label={`${term("Task")} name *`}>
            <input
              required
              value={form.task_name}
              onChange={(e) => setForm({ ...form, task_name: e.target.value })}
              className={inp}
              style={border}
              data-testid="task-name-input"
            />
          </F>
          <F label="Description">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={inp + " min-h-[70px] py-2"}
              style={border}
            />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Assign to *">
              <select
                required
                value={form.assigned_to_id}
                onChange={(e) => setForm({ ...form, assigned_to_id: e.target.value })}
                className={inp}
                style={border}
                data-testid="task-assignee-select"
              >
                <option value="">Select…</option>
                {teamMembers.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.full_name || m.email}
                  </option>
                ))}
              </select>
            </F>
            <F label="Due date *">
              <input
                required
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className={inp}
                style={border}
                data-testid="task-due-input"
              />
            </F>
            <F label="Priority">
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className={inp}
                style={border}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </F>
          </div>

          <F label={`Related ${term("Deals")}`}>
            <DealMultiPicker
              selected={selectedDeals}
              onChange={setSelectedDeals}
              fallbackPipelineId={defaultPipelineId}
              testidPrefix="task-related-deals"
            />
          </F>

          <label className="flex items-center gap-2 text-sm mt-2">
            <input
              type="checkbox"
              checked={form.reminder_enabled}
              onChange={(e) => setForm({ ...form, reminder_enabled: e.target.checked })}
              data-testid="task-reminder-toggle"
            />
            Send a reminder
          </label>
          {form.reminder_enabled && (
            <F label="Remind on">
              <input
                type="date"
                value={form.remind_on || ""}
                onChange={(e) => setForm({ ...form, remind_on: e.target.value })}
                className={inp}
                style={border}
                data-testid="task-remind-on"
              />
            </F>
          )}

          <label className="flex items-center gap-2 text-sm mt-2">
            <input
              type="checkbox"
              checked={form.reviewer_enabled}
              onChange={(e) =>
                setForm({ ...form, reviewer_enabled: e.target.checked })
              }
              data-testid="task-reviewer-toggle"
            />
            Set a reviewer
          </label>
          {form.reviewer_enabled && (
            <F label="Reviewer">
              <select
                value={form.reviewer_id || ""}
                onChange={(e) => setForm({ ...form, reviewer_id: e.target.value })}
                className={inp}
                style={border}
                data-testid="task-reviewer-select"
              >
                <option value="">Select…</option>
                {teamMembers.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.full_name || m.email}
                  </option>
                ))}
              </select>
            </F>
          )}

          {prefill?.duplicated_from_task_id && (
            <div
              className="text-xs px-2 py-1.5 rounded"
              style={{ background: "rgba(40,42,116,0.05)", color: "var(--cw-muted)" }}
            >
              Following up from a completed {term("task").toLowerCase()} — every field is
              editable before you create.
            </div>
          )}
        </div>
        <div className="px-6 py-3 border-t flex justify-end gap-2" style={border}>
          <button
            type="button"
            onClick={onClose}
            className="cw-btn-ghost text-sm"
            data-testid="task-modal-cancel"
          >
            Cancel
          </button>
          <button
            disabled={saving}
            type="submit"
            className="cw-btn-primary text-sm"
            data-testid="task-modal-submit"
          >
            {saving ? "Saving…" : submitLabel || `Create ${term("Task")}`}
          </button>
        </div>
      </form>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="cw-caption uppercase tracking-widest">{label}</label>
      {children}
    </div>
  );
}
